import { Action, ActionRunDetail } from '../types/actionTypes';
import { actionInitiatorIp } from './actionPresentation';
import { resolveActionDisplayStatus } from './actionStatusConfig';

export const actionRunDetailToAction = (run: ActionRunDetail): Action => ({
  id: run.id,
  userId: run.userId ?? 0,
  username: run.username,
  actionType: run.actionType,
  comment: run.comment,
  fileName: run.fileName,
  status: run.status,
  aggregateStatus: run.status,
  actionPhase: run.phaseCode ?? 'INITIALIZED',
  createdAt: run.createdAt ?? new Date().toISOString(),
  startedAt: run.startedAt,
  completedAt: run.completedAt,
  initiatingUserIp: actionInitiatorIp(run),
  totalRtus: run.totalTargets,
  successfulRtus: run.successfulTargets,
  failedRtus: run.failedTargets,
  runningRtus: run.runningTargets,
  cancelledRtus: run.cancelledTargets,
  durationMs: run.durationMs,
  requiresFileUpload: run.requiresFileUpload,
  retrySourceActionId: run.retrySourceActionId,
  commandHints: run.commandHints,
  artifacts: run.artifacts,
  revision: run.revision,
  downloadSelection: run.downloadSelection,
  actionDetails: (run.targetRuns ?? []).map((target) => ({
    id: target.id,
    rtuId: target.rtuId,
    rtuSerialNo: target.rtuSerialNo,
    rtuName: target.rtuName,
    ip: target.ip ?? '',
    status: target.status,
    phase: target.phaseCode ?? 'INITIALIZED',
    phaseLabel: target.phaseLabel,
    responseMessage: target.responseMessage,
    startedAt: target.startedAt,
    completedAt: target.completedAt,
    durationMs: target.durationMs,
    timeline: target.timeline,
    commandHints: target.commandHints,
    revision: target.version,
    transfer: target.transfer,
  })),
});

/**
 * Converts the normalized Redux snapshot back to the detail contract used by
 * action surfaces. This keeps the two DTOs explicit rather than relying on an
 * unsafe assertion when a WebSocket snapshot updates a visible detail panel.
 *
 * `status`/`runStatus` go through `resolveActionDisplayStatus` rather than a
 * bare `aggregateStatus ?? status` fallback, so a run rendered here (the
 * workbench list and detail panel) always agrees with the tracker, which
 * resolves the same `Action` the same way.
 */
export const actionToActionRunDetail = (action: Action): ActionRunDetail => ({
  id: action.id,
  userId: action.userId,
  username: action.username,
  actionType: action.actionType,
  comment: action.comment,
  fileName: action.fileName,
  status: resolveActionDisplayStatus(action),
  phaseCode: action.actionPhase,
  createdAt: action.createdAt,
  startedAt: action.startedAt,
  completedAt: action.completedAt,
  initiatorIp: action.initiatingUserIp,
  durationMs: action.durationMs,
  totalTargets: action.totalRtus,
  successfulTargets: action.successfulRtus,
  failedTargets: action.failedRtus,
  runningTargets: action.runningRtus,
  cancelledTargets: action.cancelledRtus,
  requiresFileUpload: action.requiresFileUpload,
  retrySourceActionId: action.retrySourceActionId,
  commandHints: action.commandHints,
  artifacts: action.artifacts,
  revision: action.revision,
  downloadSelection: action.downloadSelection,
  targetRuns: action.actionDetails.map((detail) => ({
    id: detail.id ?? 0,
    actionRunId: action.id,
    userId: action.userId,
    username: action.username,
    actionType: action.actionType,
    runStatus: resolveActionDisplayStatus(action),
    runPhaseCode: action.actionPhase,
    retrySourceActionId: action.retrySourceActionId,
    status: detail.status,
    phaseCode: detail.phase,
    phaseLabel: detail.phaseLabel,
    rtuId: detail.rtuId,
    rtuSerialNo: detail.rtuSerialNo,
    rtuName: detail.rtuName,
    ip: detail.ip,
    responseMessage: detail.responseMessage,
    plannedAt: action.createdAt,
    startedAt: detail.startedAt,
    completedAt: detail.completedAt,
    durationMs: detail.durationMs,
    commandHints: detail.commandHints,
    version: detail.revision,
    timeline: detail.timeline,
    transfer: detail.transfer,
  })),
});

export const isActionRunDetailPayload = (value: unknown): value is ActionRunDetail => {
  const candidate = value as Partial<ActionRunDetail> | null;
  return Boolean(candidate && Number.isFinite(candidate.id) && ('targetRuns' in candidate || 'phaseCode' in candidate));
};
